/**
 * Zine "Issue" design tokens — the class page as a magazine issue.
 * Single light theme always (a printed zine: cream paper, poster red,
 * protest-press black) regardless of the app theme.
 *
 * Values are copied 1:1 from the design spec's CSS custom properties.
 */

/** Core palette (spec `:root` custom properties). */
export const zineColors = {
  paper: '#FFF8F5',
  cream: '#FEF3EF',
  ink: '#1A1018',
  mid: '#6B4F55',
  warmGray: '#756560',
  red: '#D41920',
  redApplied: '#CF2E2E',
  sizzle: '#E84B5A',
  charm: '#F2A0C4',
  baby: '#F8D7DD',
  gold: '#FFD700',
  line: '#F0DAD5',
  good: '#2E7D5B',
  goodSoft: '#E7F4EC',
} as const;

/** Supporting literals that appear inline in the spec CSS. */
export const zineExtraColors = {
  /** bottom stop of the cover's halftone gradient */
  coverGradientEnd: '#B5131A',
  /** cover dek text tint */
  dekTint: '#FCE3E0',
  /** muted text on dark bands (agenda small / back-cover sub & teaser) */
  mutedOnDark: '#C9AEB4',
  /** speak-panel description text */
  speakMuted: '#D9C7CC',
  /** hard shadow under the back-cover CTA */
  ctaShadow: '#8E1218',
  /** mp4 mini-poster background */
  posterDark: '#2A1B23',
  /** verdict box border */
  verdictBorder: '#BFDECB',
  /** blank input placeholder */
  blankPlaceholder: '#D3A9B4',
  /** middle stop of the pill-button gradient */
  gradientMid: '#F05F7A',
  /** end stop of the pill-button gradient */
  gradientEnd: '#F78DA7',
} as const;

/**
 * Font stacks. The p2e app loads 'Oswald' and 'DM Serif Text' from Google
 * Fonts (apps/p2e/index.html). 'Montserrat' is NOT currently loaded — the
 * integration agent adds the font link; until then the spec's stand-in
 * fallbacks render.
 */
export const zineFonts = {
  /** condensed display face — spec --font-cond (stand-ins kept as fallbacks) */
  cond: "'Oswald', 'Avenir Next Condensed', 'Arial Narrow', 'Helvetica Neue', sans-serif",
  /** serif italic face — spec --font-serif */
  serif: "'DM Serif Text', Georgia, 'Times New Roman', serif",
  /** body/UI face — spec --font-ui */
  ui: "'Montserrat', 'Avenir Next', 'Segoe UI', system-ui, sans-serif",
} as const;

/** Pill-button gradient (spec .btn / .cta background). */
export const zineButtonGradient =
  'linear-gradient(90deg, #CF2E2E, #F05F7A 55%, #F78DA7)';

export interface ZinePairColor {
  /** saturated print hue — borders, badges */
  solid: string;
  /** paper-tint of the same hue — card backgrounds */
  soft: string;
}

/**
 * Distinguishable poster-print hues for exercises that color-code
 * correspondences (matching pairs, scramble words). Flat "spot ink on paper"
 * tones rather than the app's tailwind pastels, so they stay in the zine
 * language while giving students a visual link between related items.
 */
export const zinePairColors: readonly ZinePairColor[] = [
  { solid: '#D41920', soft: '#F8D7DD' }, // poster red
  { solid: '#C79100', soft: '#FFF1C2' }, // print gold
  { solid: '#2E7D5B', soft: '#E7F4EC' }, // stamp green
  { solid: '#7C3AAB', soft: '#EFE2F8' }, // plum
  { solid: '#0E7490', soft: '#DFF1F5' }, // teal ink
  { solid: '#D96A1B', soft: '#FBE7D6' }, // safety orange
  { solid: '#D4589A', soft: '#FAE0EE' }, // magenta
  { solid: '#2B5FB8', soft: '#E4EBF9' }, // workbook blue
] as const;

/** Stable palette pick for an item index (wraps past the palette length). */
export function zinePairColorFor(index: number): ZinePairColor {
  return zinePairColors[((index % zinePairColors.length) + zinePairColors.length) % zinePairColors.length];
}

/** Root class that scopes all zine styles (see ZineStyles / ZineIssue). */
export const ZINE_ROOT_CLASS = 'z-issue';

/** "01"-style zero padding for spread / agenda numbers. */
export function zineZeroPad(no: number | string): string {
  return typeof no === 'number' ? String(no).padStart(2, '0') : no;
}
